import { createApi } from '@reduxjs/toolkit/query/react'
import {
  GetOrderPayload,
  GetOrdersResult,
  Order,
  OrderStatus,
  OrderEventCompanySuggestion,
  EventType,
  UpdateOrderEventsPayload,
  UpdateOrderPayload,
  UpdateStatusPayload,
  CreateOrderBody,
  CreateCarrierLoadConfirmationBody,
} from './orders.types'
import { createBaseQueryWithReauth } from 'store/auth/base-query-with-reauth'
import { Pagination } from 'store/common.types'
import { sortOrders } from './orders.utils'

const baseUrl = `${process.env.REACT_APP_ORDER_SERVICE_API}`

export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: createBaseQueryWithReauth(baseUrl!),
  tagTypes: ['Orders', 'Order'],
  // Fix: Prevent stale cache - don't keep unused cached data to ensure fresh data on refetch
  keepUnusedDataFor: 0,
  // Fix: Force refetch when component mounts or query arguments change - ensures latest data from server
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    listNewOrders: builder.query<GetOrdersResult, Pagination>({
      query: ({ page, size }) => ({
        url: `osapi/v1/dispatch/new?page=${page}&size=${size}`,
      }),
      transformResponse: (response: GetOrdersResult) => sortOrders(response),
      providesTags: ['Orders'],
    }),

    listActiveOrders: builder.query<GetOrdersResult, Pagination>({
      query: ({ page, size }) => ({
        url: `osapi/v1/dispatch/assigned?page=${page}&size=${size}`,
      }),
      transformResponse: (response: GetOrdersResult) => sortOrders(response),
      providesTags: ['Orders'],
    }),

    listArchivedOrders: builder.query<GetOrdersResult, Pagination>({
      query: ({ page, size }) => ({
        url: `osapi/v1/dispatch/delivered?page=${page}&size=${size}`,
      }),
      transformResponse: (response: GetOrdersResult) => sortOrders(response),
      providesTags: ['Orders'],
    }),

    getOrderById: builder.query<Order, GetOrderPayload>({
      query: ({ orderId }) => ({
        url: `osapi/v1/orders/${orderId}`,
      }),
      providesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
      ],
    }),

    createOrder: builder.mutation<Order, CreateOrderBody>({
      query: (body) => ({
        url: 'osapi/v1/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Orders'],
    }),

    createCarrierLoadConfirmation: builder.mutation<
      Order,
      CreateCarrierLoadConfirmationBody
    >({
      query: (body) => ({
        url: 'osapi/v1/orders/carrier-load-confirmation',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Orders'],
    }),

    getCarrierLoadConfirmations: builder.query<Order[], { sourceOrderId: number }>({
      query: ({ sourceOrderId }) => ({
        url: `osapi/v1/orders/${sourceOrderId}/carrier-load-confirmations`,
      }),
      providesTags: (_result, _error, { sourceOrderId }) => [
        { type: 'Order', id: sourceOrderId },
      ],
    }),

    generateDefaultLoadConfirmationPdf: builder.mutation<
      { s3Key: string },
      { orderId: number }
    >({
      query: ({ orderId }) => ({
        url: `osapi/v1/orders/${orderId}/default-load-confirmation-pdf`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'Orders',
      ],
    }),

    updateOrder: builder.mutation<Order, UpdateOrderPayload>({
      query: ({ orderId, order }) => ({
        url: `osapi/v1/orders/${orderId}`,
        method: 'PUT',
        body: { ...order },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'Orders',
      ],
    }),

    updateOrderEvents: builder.mutation<Order, UpdateOrderEventsPayload>({
      query: ({ order, orderId, orderEventId }) => ({
        url: `osapi/v1/orders/${orderId}/events/${orderEventId}}`,
        method: 'PUT',
        body: { ...order },
      }),
    }),

    getOrderStatus: builder.query<OrderStatus[], GetOrderPayload>({
      query: ({ orderId }) => ({
        url: `osapi/v1/orders/orderstatus/${orderId}`,
      }),
      providesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
      ],
    }),

    updateOrderStatus: builder.mutation<any, UpdateStatusPayload>({
      query: ({ orderId, status }) => ({
        url: `osapi/v1/orders/${orderId}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'Orders',
      ],
    }),

    deleteOrder: builder.mutation<void, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `osapi/v1/orders/${orderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'Orders',
      ],
    }),

    deleteOrderEvent: builder.mutation<
      void,
      { orderId: number; orderEventId: number }
    >({
      query: ({ orderId, orderEventId }) => ({
        url: `osapi/v1/orders/${orderId}/events/${orderEventId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'Orders',
      ],
    }),

    getOrderEventCompanySuggestions: builder.query<
      OrderEventCompanySuggestion[],
      { eventType: EventType }
    >({
      query: ({ eventType }) => ({
        url: `osapi/v1/orders/events/company-suggestions?eventType=${eventType}`,
      }),
    }),
  }),
})

export const {
  useListNewOrdersQuery,
  useListActiveOrdersQuery,
  useListArchivedOrdersQuery,
  useLazyListNewOrdersQuery,
  useLazyListActiveOrdersQuery,
  useLazyListArchivedOrdersQuery,
  useGetOrderByIdQuery,
  useLazyGetOrderByIdQuery,
  useCreateOrderMutation,
  useCreateCarrierLoadConfirmationMutation,
  useLazyGetCarrierLoadConfirmationsQuery,
  useGenerateDefaultLoadConfirmationPdfMutation,
  useUpdateOrderMutation,
  useUpdateOrderEventsMutation,
  useGetOrderStatusQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useDeleteOrderEventMutation,
  useGetOrderEventCompanySuggestionsQuery,
} = ordersApi
